import React, { useState, useEffect } from 'react';
import { getDynamicPlayerHeadshot } from '../utils/playerData';
import { fetchTeamData, getTeamLogo, parseGameTeams } from '../utils/teamData';

const PickCard = ({ pick, game, onAddToParlay, isInParlay, onCardClick, league = 'nba' }) => {
  const [teamData, setTeamData] = useState(null);
  const [playerHeadshot, setPlayerHeadshot] = useState(null);
  const [activePopup, setActivePopup] = useState(null);

  const pickLeague = pick.league || game.league || league;

  useEffect(() => {
    const loadData = async () => {
      const teamDataResult = await fetchTeamData(pickLeague);
      setTeamData(teamDataResult);
      
      // Fetch headshot for this player (use player_id from JSON if available)
      const headshot = await getDynamicPlayerHeadshot(
        pick.player,
        pick.player_id,
        pickLeague,
        pick.espn_id
      );
      setPlayerHeadshot(headshot);
    };
    loadData();
  }, [pick.player, pick.player_id, pick.espn_id, pickLeague]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedHitRateItem = event.target.closest('.hit-rate-item.clickable');
      const clickedPopup = event.target.closest('.hit-rate-popup');
      
      if (!clickedHitRateItem && !clickedPopup && activePopup) {
        setActivePopup(null);
      }
    };

    if (activePopup) {
      // Small delay to avoid immediate closing
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activePopup]);

  const getHitRateExplanation = (type, hitRate) => {
    const num = hitRate !== null && hitRate !== undefined ? parseFloat(hitRate) : null;
    const percentage = num !== null ? `${num.toFixed(1)}%` : 'N/A';
    const { awayTeam, homeTeam } = teamData ? parseGameTeams(game.game) : { awayTeam: '', homeTeam: '' };
    
    // For H2H, try to determine opponent team
    // If prop mentions a team name, use the other team; otherwise default to home team
    let opponentTeam = homeTeam;
    if (pick.prop && teamData) {
      const propLower = pick.prop.toLowerCase();
      if (propLower.includes(awayTeam.toLowerCase())) {
        opponentTeam = homeTeam;
      } else if (propLower.includes(homeTeam.toLowerCase())) {
        opponentTeam = awayTeam;
      }
    }

    const explanations = {
      'L5': `Player has hit this ${percentage} of the last 5 games`,
      'L10': `Player has hit this ${percentage} of the last 10 games`,
      'L20': `Player has hit this ${percentage} of the last 20 games`,
      'H2H': opponentTeam ? `Player has hit this ${percentage} of the time against ${opponentTeam}` : `Player has hit this ${percentage} of the time head-to-head`,
      '2024': `Player has hit this ${percentage} of the time during the 2024 season`,
      'L50': `Player has hit this ${percentage} of the last 50 games`
    };

    return explanations[type] || '';
  };

  const handleHitRateClick = (type, hitRate) => {
    if (hitRate === null || hitRate === undefined) return;
    if (activePopup === type) {
      setActivePopup(null);
    } else {
      setActivePopup(type);
    }
  };

  const getPlayerInitials = (playerName) => {
    const names = playerName.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return playerName.substring(0, 2).toUpperCase();
  };

  const formatOdds = (odds) => {
    if (!odds) return 'N/A';
    if (odds > 0) {
      return `+${odds}`;
    }
    return odds.toString();
  };

  const formatHitRate = (hitRate) => {
    if (hitRate === null || hitRate === undefined) return 'N/A';
    // Round to 1 decimal place if it's a number
    const num = typeof hitRate === 'number' ? hitRate : parseFloat(hitRate);
    if (isNaN(num)) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  const formatNoVigProb = (prob) => {
    if (prob === null || prob === undefined) return 'N/A';
    const num = typeof prob === 'number' ? prob : parseFloat(prob);
    if (isNaN(num)) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  const getDefensiveRankColor = (rank) => {
    if (!rank || rank === null || rank === undefined) return '';
    const rankNum = typeof rank === 'number' ? rank : parseFloat(rank);
    if (isNaN(rankNum)) return '';
    // Lower rank = better defense (1 is best, 30 is worst)
    // Good defense (rank 1-10) = red (bad for offense)
    // Bad defense (rank 21-30) = green (good for offense)
    if (rankNum <= 10) return 'def-rank-good'; // Good defense = red
    if (rankNum >= 21) return 'def-rank-bad'; // Bad defense = green
    return 'def-rank-neutral'; // Middle = neutral
  };

  // Get defensive ranking from pick data
  const getDefensiveRank = () => {
    // Try different possible field names
    if (pick.defensive_analysis) {
      // First try: direct opponent_points_rank
      if (pick.defensive_analysis.opponent_points_rank !== null && pick.defensive_analysis.opponent_points_rank !== undefined) {
        return pick.defensive_analysis.opponent_points_rank;
      }
      // Second try: defensive_rankings dict
      if (pick.defensive_analysis.defensive_rankings && pick.defensive_analysis.defensive_rankings.points_allowed) {
        // defensive_rankings is a dict mapping team_id to rank
        // Try to find opponent team ID from game data
        if (teamData && game) {
          const { awayTeam, homeTeam } = parseGameTeams(game.game);
          const opponentTeam = pick.player_team === awayTeam ? homeTeam : awayTeam;
          const opponentTeamId = teamData.teams?.find(t => t.name === opponentTeam)?.id;
          if (opponentTeamId && pick.defensive_analysis.defensive_rankings.points_allowed[opponentTeamId]) {
            return pick.defensive_analysis.defensive_rankings.points_allowed[opponentTeamId];
          }
        }
      }
    }
    // Try direct field on pick
    if (pick.opponent_points_rank !== null && pick.opponent_points_rank !== undefined) {
      return pick.opponent_points_rank;
    }
    if (pick.defensive_rank !== null && pick.defensive_rank !== undefined) {
      return pick.defensive_rank;
    }
    return null;
  };

  const getHitRateColorClass = (hitRate) => {
    if (hitRate === null || hitRate === undefined) return '';
    const num = typeof hitRate === 'number' ? hitRate : parseFloat(hitRate);
    if (isNaN(num)) return '';
    
    if (num < 20) return 'hit-rate-red';
    if (num > 80) return 'hit-rate-green';
    return 'hit-rate-yellow';
  };

  const { awayTeam, homeTeam } = teamData ? parseGameTeams(game.game) : { awayTeam: '', homeTeam: '' };
  const awayLogo = teamData ? getTeamLogo(awayTeam, teamData) : null;
  const homeLogo = teamData ? getTeamLogo(homeTeam, teamData) : null;
  
  return (
    <div className="pick-card-horizontal" onClick={() => onCardClick && onCardClick(pick, game)} style={{ cursor: 'pointer' }}>
      {/* Add to Parlay Button */}
      <button 
        className={`parlay-add-btn ${isInParlay ? 'in-parlay' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onAddToParlay && onAddToParlay(pick, game);
        }}
        title={isInParlay ? 'Already in parlay' : 'Add to parlay'}
      >
        {isInParlay ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
      </button>

      {/* Left Section: Player Image, Name, Prop, Game */}
      <div className="pick-card-top-row">
        {/* Player Image */}
        <div className="player-image-container">
          <div className="player-avatar-horizontal">
            {playerHeadshot ? (
              <img 
                src={playerHeadshot} 
                alt={pick.player}
                className="player-headshot-horizontal"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="player-initials-horizontal" style={{ display: playerHeadshot ? 'none' : 'flex' }}>
              {getPlayerInitials(pick.player)}
            </div>
          </div>
        </div>

        {/* Player Name */}
        <div className="player-name-container">
          <h3 className="player-name-horizontal">{pick.player}</h3>
        </div>

        {/* Player Prop */}
        <div className="player-prop-container">
          <p className="player-prop-horizontal">{pick.prop}</p>
        </div>

        {/* Game with Team Logos */}
        <div className="game-info-container">
          {teamData && (
            <div className="game-teams-horizontal">
              <div className="team-with-logo-horizontal">
                {awayLogo && <img src={awayLogo} alt={awayTeam} className="team-logo-horizontal" />}
                <span className="team-name-horizontal">{awayTeam}</span>
              </div>
              <span className="vs-horizontal">@</span>
              <div className="team-with-logo-horizontal">
                {homeLogo && <img src={homeLogo} alt={homeTeam} className="team-logo-horizontal" />}
                <span className="team-name-horizontal">{homeTeam}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Section: Odds with No-Vig, Hit Rates, Defensive Rank */}
      <div className="pick-card-bottom-row">
        {/* First Row: Odds with No-Vig and Defensive Rank */}
        <div className="odds-vig-row">
          <div className="odds-container">
            <div className="odds-content">
              <img src="/logos/DraftKings_logo.png" alt="DraftKings" className="draftkings-logo" />
              <span className="odds-label-inline">Odds:</span>
              <span className="odds-over">O {formatOdds(pick.over_odds)}</span>
              <span className="odds-separator">/</span>
              <span className="odds-under">U {formatOdds(pick.under_odds)}</span>
              <span className="no-vig-separator">|</span>
              <span className="no-vig-label">No-Vig:</span>
              <span className="no-vig-over">{formatNoVigProb(pick.over_prob_no_vig)}</span>
              <span className="no-vig-separator">/</span>
              <span className="no-vig-under">{formatNoVigProb(pick.under_prob_no_vig)}</span>
            </div>
          </div>

          {/* Defensive Ranking */}
          {(() => {
            const defRank = getDefensiveRank();
            // Debug: log to see what we're getting
            if (defRank === null || defRank === undefined) {
              console.log('No defensive rank found for pick:', pick.player, pick.prop, {
                hasDefensiveAnalysis: !!pick.defensive_analysis,
                defensiveAnalysis: pick.defensive_analysis,
                opponentPointsRank: pick.opponent_points_rank,
                defensiveRank: pick.defensive_rank
              });
            }
            if (defRank !== null && defRank !== undefined) {
              return (
                <div className="defensive-rank-container">
                  <span className="def-rank-label">Def Rank:</span>
                  <span className={`def-rank-value ${getDefensiveRankColor(defRank)}`}>
                    {Math.round(defRank)}
                  </span>
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* Second Row: Hit Rates - All on one line */}
        <div className="hit-rates-row">

          {/* Last 5 Hit % */}
          <div 
            className={`hit-rate-item-compact ${pick.hit_rate_last_5 !== null && pick.hit_rate_last_5 !== undefined ? 'clickable' : ''} ${activePopup === 'L5' ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleHitRateClick('L5', pick.hit_rate_last_5);
            }}
            style={{ position: 'relative' }}
          >
            <span className="hit-rate-badge-compact">L5</span>
            <span className={`hit-rate-value-compact ${getHitRateColorClass(pick.hit_rate_last_5)}`}>{formatHitRate(pick.hit_rate_last_5)}</span>
            {activePopup === 'L5' && (
              <div className="hit-rate-popup">
                <div className="hit-rate-popup-content">
                  {getHitRateExplanation('L5', pick.hit_rate_last_5)}
                </div>
              </div>
            )}
          </div>

          {/* Last 10 Hit % */}
          <div 
            className={`hit-rate-item-compact ${pick.hit_rate_last_10 !== null && pick.hit_rate_last_10 !== undefined ? 'clickable' : ''} ${activePopup === 'L10' ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleHitRateClick('L10', pick.hit_rate_last_10);
            }}
            style={{ position: 'relative' }}
          >
            <span className="hit-rate-badge-compact">L10</span>
            <span className={`hit-rate-value-compact ${getHitRateColorClass(pick.hit_rate_last_10)}`}>{formatHitRate(pick.hit_rate_last_10)}</span>
            {activePopup === 'L10' && (
              <div className="hit-rate-popup">
                <div className="hit-rate-popup-content">
                  {getHitRateExplanation('L10', pick.hit_rate_last_10)}
                </div>
              </div>
            )}
          </div>

          {pickLeague !== 'nfl' && (
            <div 
              className={`hit-rate-item-compact ${pick.hit_rate_last_20 !== null && pick.hit_rate_last_20 !== undefined ? 'clickable' : ''} ${activePopup === 'L20' ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleHitRateClick('L20', pick.hit_rate_last_20);
              }}
              style={{ position: 'relative' }}
            >
              <span className="hit-rate-badge-compact">L20</span>
              <span className={`hit-rate-value-compact ${getHitRateColorClass(pick.hit_rate_last_20)}`}>{formatHitRate(pick.hit_rate_last_20)}</span>
              {activePopup === 'L20' && (
                <div className="hit-rate-popup">
                  <div className="hit-rate-popup-content">
                    {getHitRateExplanation('L20', pick.hit_rate_last_20)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* H2H (vs Team) Hit % */}
          <div 
            className={`hit-rate-item-compact ${pick.hit_rate_vs_team !== null && pick.hit_rate_vs_team !== undefined ? 'clickable' : ''} ${activePopup === 'H2H' ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleHitRateClick('H2H', pick.hit_rate_vs_team);
            }}
            style={{ position: 'relative' }}
          >
            <span className="hit-rate-badge-compact">H2H</span>
            <span className={`hit-rate-value-compact ${getHitRateColorClass(pick.hit_rate_vs_team)}`}>{formatHitRate(pick.hit_rate_vs_team)}</span>
            {activePopup === 'H2H' && (
              <div className="hit-rate-popup">
                <div className="hit-rate-popup-content">
                  {getHitRateExplanation('H2H', pick.hit_rate_vs_team)}
                </div>
              </div>
            )}
          </div>

          {/* Last Season Hit % */}
          <div 
            className={`hit-rate-item-compact ${pick.hit_rate_last_season !== null && pick.hit_rate_last_season !== undefined ? 'clickable' : ''} ${activePopup === '2024' ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleHitRateClick('2024', pick.hit_rate_last_season);
            }}
            style={{ position: 'relative' }}
          >
            <span className="hit-rate-badge-compact">2024</span>
            <span className={`hit-rate-value-compact ${getHitRateColorClass(pick.hit_rate_last_season)}`}>{formatHitRate(pick.hit_rate_last_season)}</span>
            {activePopup === '2024' && (
              <div className="hit-rate-popup">
                <div className="hit-rate-popup-content">
                  {getHitRateExplanation('2024', pick.hit_rate_last_season)}
                </div>
              </div>
            )}
          </div>

          {pickLeague !== 'nfl' && (
            <div 
              className={`hit-rate-item-compact ${pick.hit_rate_last_50 !== null && pick.hit_rate_last_50 !== undefined ? 'clickable' : ''} ${activePopup === 'L50' ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleHitRateClick('L50', pick.hit_rate_last_50);
              }}
              style={{ position: 'relative' }}
            >
              <span className="hit-rate-badge-compact">L50</span>
              <span className={`hit-rate-value-compact ${getHitRateColorClass(pick.hit_rate_last_50)}`}>{formatHitRate(pick.hit_rate_last_50)}</span>
              {activePopup === 'L50' && (
                <div className="hit-rate-popup">
                  <div className="hit-rate-popup-content">
                    {getHitRateExplanation('L50', pick.hit_rate_last_50)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PickCard;
